import {useGetPatientTherapySequence} from '@/modules/patients-therapies/api'
import {describePrintError} from '@/modules/preparation-label/format'
import {useLabelTranslate} from '@/modules/preparation-label/use-label-translate'
import {useGetSettings} from '@/modules/settings/api'
import {queries} from '@/shared/integrations/zero'
import {openPdfBlob} from '@/shared/utils/open-pdf'
import {toaster} from '@cypro/ui/toaster'
import {pdf} from '@react-pdf/renderer'
import {useQuery} from '@rocicorp/zero/react'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'
import {buildReportData} from './build-print-data'
import {getStatusLogEntry} from '@/modules/preparation-label/map-label-source'
import {PreparationReportPdf} from './preparation-report-pdf'
import type {ReportTranslate} from './types'

export function usePrintPreparationReport(sequenceId: string | undefined) {
  const {t} = useTranslate('cleanroom')
  const {settings} = useGetSettings()
  const [source] = useQuery(queries.patientsTherapies.getPreparationLabelSource(sequenceId ?? ''))
  const [statusLogs] = useQuery(queries.patientsTherapies.getSequenceStatusLogs(sequenceId ?? ''))
  const [sequence] = useGetPatientTherapySequence(sequenceId)
  const [isPrinting, setIsPrinting] = useState(false)
  const getLabelTranslate = useLabelTranslate()

  const isCompleted = getStatusLogEntry(statusLogs, 'COMPLETE_PRODUCTION') !== null
  const canPrint = Boolean(source && settings && sequence) && isCompleted
  const disabledReason = canPrint ? undefined : t('preparation_report.not_available_yet')

  async function print() {
    if (!source || !settings || !sequence) return
    setIsPrinting(true)
    try {
      const labelT = await getLabelTranslate()
      const reportT: ReportTranslate = (key, params) => t(key, params)
      const {report, labelCopy} = await buildReportData({
        source,
        statusLogs,
        sequence,
        settings,
        labelTranslate: labelT,
        reportTranslate: reportT,
      })

      const blob = await pdf(
        <PreparationReportPdf
          report={report}
          labelCopy={labelCopy}
          logoSrc={settings.logoUrl ?? null}
          pageLabel={(page, total) => t('preparation_report.page', {page, total})}
        />
      ).toBlob()

      openPdfBlob(blob, t('preparation_report.popup_blocked'))
    } catch (error) {
      toaster.error({
        title: t('preparation_report.print_failed'),
        description: describePrintError(error, t),
      })
    } finally {
      setIsPrinting(false)
    }
  }

  return {print, isPrinting, canPrint, disabledReason}
}
